/**
 * Someone rang me into a huddle (HuddleViewModel `incoming`, from the realtime `notification` about a huddle; design
 * "Huddle ring"). Web: a card in the corner (IncomingRing) that slides up and fades out. Desktop: the call window
 * above every app (ringWindow.ts, src-tauri/src/ring.rs) instead, whose buttons come back here. Either way: mic and
 * camera to join with (the huddle prefs, remembered), Join (leaves any other call, joins this one, opens the call
 * view), Decline ("Not now" to the caller), "Decline with a message…" (declines and opens the DM with the caller), and
 * on desktop × (stops ringing here only). 30 s without an answer, the huddle ending, or answering on another device
 * dismisses it. While it shows, the ringtone loops (notificationSound.ts `startRingtone`), like Google Meet.
 */
import { useEffect, useRef, useState } from "react";
import { Profile, Shell, type Huddles, type ShellViewModel } from "@bundle/core";
import { IncomingRing, useHuddle, useMediaResolver, useT, useWorkspace } from "@bundle/ui";
import { notificationSound } from "../platform/notificationSound.ts";
import { showDesktopRingBanner } from "../platform/notifier.ts";
import { onRingAction, ringsInWindow, showRingWindow } from "../platform/ringWindow.ts";

const PLACE = { position: "fixed", right: 16, bottom: 16, zIndex: 50, width: "min(360px, calc(100vw - 32px))" } as const;
/** The card's fade-out (primitives call.css `.b-ring[data-leaving]`). */
const LEAVE_MS = 200;

type Ring = Huddles.IncomingRing;

export function IncomingHuddle({ shell, slug }: { shell: ShellViewModel; slug: string }) {
  const t = useT();
  const huddle = useHuddle();
  const ws = useWorkspace();
  const media = useMediaResolver();
  const ring = huddle?.state.incoming;
  const ringing = huddle === null ? undefined : ring?.huddleId;
  const prefs = huddle?.state.prefs;
  const caller = ring === undefined ? undefined : ws.state.cache?.members[ring.callerId];
  const look = caller === undefined ? {} : Profile.avatarLook(caller);
  const callerName = caller?.displayName ?? ring?.title ?? "";
  useEffect(() => (ringing === undefined ? undefined : notificationSound.startRingtone()), [ringing]);

  const act = (r: Ring, action: "join" | "decline" | "message" | "close", mic: boolean, camera: boolean) => {
    if (huddle === null) return;
    if (action === "close") return huddle.vm.silenceRing();
    if (action === "decline") return huddle.vm.declineRing();
    if (action === "message") {
      huddle.vm.declineRing();
      shell.open(Shell.formatUrl({ type: "dm", ws: slug, userId: r.callerId }), "current");
      return;
    }
    huddle.vm.setJoinWithMicOff(!mic);
    huddle.vm.setJoinWithCameraOff(!camera);
    if (huddle.browserOnly) {
      huddle.vm.declineRing();
      huddle.openInBrowser(r.channelId);
      return;
    }
    void huddle.vm.acceptRing();
    shell.open(Shell.formatUrl({ type: "huddle", ws: slug, huddleId: r.channelId }), "current");
  };
  // The call window's buttons act on the ring it showed (read through a ref: `act` changes every render).
  const latest = useRef({ ring, act });
  latest.current = { ring, act };
  const inWindow = ringsInWindow();
  useEffect(() => {
    if (!inWindow) return;
    let off: (() => void) | undefined;
    let live = true;
    void onRingAction((a) => {
      const { ring: r, act: run } = latest.current;
      if (r !== undefined && r.huddleId === a.huddleId) run(r, a.action, a.mic, a.camera);
    }).then((o) => (live ? (off = o) : o()));
    return () => {
      live = false;
      off?.();
    };
  }, [inWindow]);
  useEffect(() => {
    if (!inWindow || ring === undefined) return;
    const src = look.src === undefined ? undefined : media.resolve(look.src);
    return showRingWindow(
      {
        huddleId: ring.huddleId,
        title: ring.title,
        body: ring.body,
        label: t("huddle.ring.windowLabel", { workspace: ws.state.cache?.workspace.name ?? "" }),
        callerName,
        ...(src === undefined ? {} : { callerPhoto: src }),
        ...(look.tone === undefined ? {} : { callerTone: look.tone }),
        ...(look.emoji === undefined ? {} : { callerEmoji: look.emoji }),
        mic: prefs?.joinWithMicOff !== true,
        camera: prefs?.joinWithCameraOff !== true,
      },
      () => showDesktopRingBanner(ring),
    );
    // Shown once per ring; later prefs changes are the window's own toggles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inWindow, ring?.huddleId]);

  // Web: the last ring stays a moment after it ends, fading out.
  const [leaving, setLeaving] = useState<Ring | undefined>(undefined);
  const shown = useRef<Ring | undefined>(undefined);
  useEffect(() => {
    if (ring !== undefined) {
      shown.current = ring;
      setLeaving(undefined);
      return;
    }
    const last = shown.current;
    shown.current = undefined;
    if (last === undefined) return;
    setLeaving(last);
    const timer = setTimeout(() => setLeaving(undefined), LEAVE_MS);
    return () => clearTimeout(timer);
  }, [ring]);

  if (inWindow || huddle === null) return null;
  const card = ring ?? leaving;
  if (card === undefined) return null;
  const mic = prefs?.joinWithMicOff !== true;
  const camera = prefs?.joinWithCameraOff !== true;
  return (
    <div style={PLACE}>
      <IncomingRing
        leaving={ring === undefined}
        title={card.title}
        body={card.body}
        caller={{ name: callerName || card.title, ...look }}
        mic={mic}
        camera={camera}
        onToggleMic={() => huddle.vm.setJoinWithMicOff(mic)}
        onToggleCamera={() => huddle.vm.setJoinWithCameraOff(camera)}
        onJoin={() => act(card, "join", mic, camera)}
        onDecline={() => act(card, "decline", mic, camera)}
        onDeclineWithMessage={() => act(card, "message", mic, camera)}
        testID="incoming-huddle"
      />
    </div>
  );
}
